import { type Articulo, type ArticuloRequest, type ArticuloResponse, type ArticuloProduccion, type ArticuloFiltered } from '../types/articulo';
import { http } from '../../../../api/http';

export class ArticuloService {
  private static readonly ENDPOINTS = {
    LIST: '/articulo/list',
    FILTERED_LIST: '/articulo/filtered-list',
    CREATE: '/articulo',
    GET_BY_ID: '/articulo',
    PRODUCCION: '/articulo/produccion',
    ORIGEN_BY_ESPECIE: '/articulo/origen',  // GET /articulo/origen/{idEspecie}
    PRODUCTOS_BY_ESPECIE_PRESENTACION: '/articulo/especie',  // GET /articulo/especie/{idEspecie}/presentacion/{idPresentacion}
  };

  public static async getArticulos(): Promise<Articulo[]> {
    try {
      const response = await http.get(this.ENDPOINTS.LIST);
      return response.data;
    } catch (error) {
      console.error('Error fetching articulos:', error);
      throw error;
    }
  }

  public static async getFilteredArticulos(): Promise<ArticuloFiltered[]> {
    try {
      const response = await http.get(this.ENDPOINTS.FILTERED_LIST);
      return response.data;
    } catch (error) {
      console.error('Error fetching filtered articulos:', error);
      throw error;
    }
  }

  public static async saveArticulo(articuloRequest: ArticuloRequest): Promise<Articulo> {
    try {
      const isUpdate = !!articuloRequest.idArticulo;

      console.log(`${isUpdate ? 'Updating' : 'Creating'} articulo:`, {
        url: this.ENDPOINTS.CREATE,
        method: 'POST',
        isUpdate,
        data: articuloRequest
      });

      const response = await http.post(this.ENDPOINTS.CREATE, articuloRequest);

      console.log(`Articulo ${isUpdate ? 'updated' : 'created'} successfully:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`Error ${articuloRequest.idArticulo ? 'updating' : 'creating'} articulo:`, error);
      throw error;
    }
  }

  // Keep the old method name for backward compatibility
  public static async createArticulo(articuloRequest: ArticuloRequest): Promise<Articulo> {
    return this.saveArticulo(articuloRequest);
  }

  public static async changeEstado(idArticulo: number, idEstado: number): Promise<Articulo> {
    try {
      const response = await http.patch(`/articulo/${idArticulo}/estado/${idEstado}`);
      return response.data;
    } catch (error) {
      console.error('Error changing articulo estado:', error);
      throw error;
    }
  }

  public static async getArticuloById(idArticulo: number): Promise<ArticuloResponse> {
    try {
      const response = await http.get(`${this.ENDPOINTS.GET_BY_ID}/${idArticulo}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching articulo by id:', error);
      throw error;
    }
  }

  public static async getArticuloProduccion(idArticulo: number): Promise<ArticuloProduccion | null> {
    try {
      const response = await http.get(`${this.ENDPOINTS.PRODUCCION}/${idArticulo}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching articulo produccion:', error);
      return null;
    }
  }

  /**
   * Obtiene el artículo por defecto según la especie del origen
   * @param idEspecie ID de la especie del DERC
   * @returns Artículo de producción por defecto para esa especie
   */
  public static async getArticuloByEspecie(idEspecie: number): Promise<ArticuloProduccion | null> {
    try {
      console.log(`Fetching default product for species ID: ${idEspecie}`);
      const response = await http.get(`${this.ENDPOINTS.ORIGEN_BY_ESPECIE}/${idEspecie}`);
      console.log('Raw API response:', response.data);

      // El API retorna un array, tomamos el primer elemento
      if (Array.isArray(response.data) && response.data.length > 0) {
        return response.data[0];
      }

      // Si no es un array, retornamos la data directamente (por compatibilidad)
      return response.data || null;
    } catch (error) {
      console.error(`Error fetching articulo by especie ${idEspecie}:`, error);
      return null;
    }
  }

  /**
   * Obtiene productos por especie y presentación para la grilla de selección
   * @param idEspecie ID de la especie
   * @param idPresentacion ID de la presentación
   * @returns Lista de productos con información de calibre y empaque interno
   */
  public static async getProductosByEspecieYPresentacion(idEspecie: number, idPresentacion: number): Promise<any[]> {
    try {
      console.log(`Fetching products for species ID: ${idEspecie} and presentation ID: ${idPresentacion}`);
      const response = await http.get(`${this.ENDPOINTS.PRODUCTOS_BY_ESPECIE_PRESENTACION}/${idEspecie}/presentacion/${idPresentacion}`);
      console.log('Products API response:', response.data);

      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error(`Error fetching products by especie ${idEspecie} and presentacion ${idPresentacion}:`, error);
      return [];
    }
  }
}